import { Component } from '../../../components/Component.js';
import { applyTranslations, getCurrentLanguage } from '../../../js/translations.js';
import { storage } from '../../../js/utils/storage.js';
import {
  getGenderPreference,
  setGenderPreference,
  getStylePreference,
  setStylePreference,
  normalizeStyle,
  getOver18,
  getNsfwEnabled,
  setAgeContentPrefs,
  getCardAnimationEnabled,
  setCardAnimationEnabled
} from '../../../js/utils/userPrefs.js';
import { GenderPreferenceModal } from '../../../js/components/GenderPreferenceModal.js';
import { isPlaceholderCharacterName } from './character-name-utils.js';
import { isSfwSite } from '../../../js/config.js';
import { showUnrestrictedBridge } from '../../../js/components/sfwBridge.js';

export class FilterComponent extends Component {
  constructor(element, options = {}) {
    super(element, options);

    this.rankingsState = options.state;
    this.onFilterChange = options.onFilterChange;
    this.currentLang = getCurrentLanguage();
    this.elementsCreated = false;
  }

  init() {
    if (!this.state || !this.state.filters) {
      this.setupState();
    }

    this.render();
    document.addEventListener('languageChanged', this.handleLanguageChange.bind(this));
    document.addEventListener('genderPreferenceSelected', this.handleGenderPreferenceSelected.bind(this));
    document.addEventListener('stylePreferenceSelected', this.handleStylePreferenceSelected.bind(this));
    document.addEventListener('nsfwPreferenceSelected', this.handleNsfwPreferenceSelected.bind(this));
  }

  setupState() {
    const initialGenderPreference = getGenderPreference();

    this.state = {
      filters: {
        nameFilter: '',
        private: false,
        male: initialGenderPreference,
        style: getStylePreference(),
        favorite: false,
        nsfw: getNsfwEnabled()
      }
    };

    if (this.rankingsState && this.rankingsState.filters) {
      const genderPreference = this.state.filters.male;
      this.state.filters = {
        ...this.state.filters,
        ...this.rankingsState.filters,
        male: this.rankingsState.filters.male !== undefined ? this.rankingsState.filters.male : genderPreference
      };
    } else {
      console.warn('FilterComponent: rankingsState or rankingsState.filters is undefined, using default values');
    }
  }

  handleLanguageChange(e) {
    if (!e.detail?.language) return;
    this.currentLang = e.detail.language;
    this.updateTranslations();
  }

  handleGenderPreferenceSelected(e) {
    if (!e.detail || e.detail.isMale === undefined) return;

    this.state.filters.male = e.detail.isMale;
    this.updateMaleFilterButton();
    this.applyFilters({ skipScroll: e.detail.skipScroll === true });
  }

  handleStylePreferenceSelected(e) {
    if (!e.detail || e.detail.style === undefined) return;

    this.state.filters.style = normalizeStyle(e.detail.style);
    this.updateStyleFilterButton();
    this.applyFilters({ skipScroll: e.detail.skipScroll === true });
  }

  handleNsfwPreferenceSelected(e) {
    if (!e.detail || e.detail.nsfw === undefined) return;
    this.state.filters.nsfw = e.detail.nsfw === true;
    this.updateNsfwFilterButton();
    this.applyFilters({ skipScroll: e.detail.skipScroll === true });
  }

  saveGenderPreference(preference) {
    try {
      setGenderPreference(preference);
      this.saveGenderPreferenceToServer(preference);
    } catch (error) {
      console.error('[FilterComponent] Error saving gender preference:', error);
    }
  }

  async saveGenderPreferenceToServer(preference) {
    try {
      const userData = storage.get('userData') || {};
      if (!userData.isLoggedIn) {
        return;
      }

      const { initOAuthManager } = await import('../../../js/utils/oauthManager.js');
      const { USER_WORKER_URL } = await import('../../../js/config.js');

      const oauthManager = initOAuthManager();
      const token = await oauthManager.getToken();

      if (!token) {
        return;
      }

      const requestUrl = `${USER_WORKER_URL}/update-profile`;
      const genderPreferenceEnum = preference ? 'male_only' : 'female_only';
      const requestBody = { interest: genderPreferenceEnum };

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server responded with status ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Unknown server error');
      }
    } catch (error) {
      console.error('[FilterComponent] Error saving gender preference to server:', error.message || error);
    }
  }


  render() {
    if (!this.elementsCreated) {
      this.createElements();
    }

    this.updateUIState();
    this.updateTranslations();
  }

  createElements() {
    const nsfwToggleHtml = `
      <div class="gender-toggle-container nsfw-toggle-container">
        <div class="gender-toggle-header">
          <span class="filtered-text" data-translate="rankings.filtered">Filtered</span>
          <span class="unrestricted-text" data-translate="rankings.unrestricted">Unrestricted</span>
        </div>
        <div class="nsfw-toggle" tabindex="0" role="button" aria-label="Toggle filtered or unrestricted content">
          <div class="gender-toggle-slider"></div>
        </div>
      </div>`;
    this.element.innerHTML = `
      <input type="text" id="name-filter" placeholder="Filter by name..." data-translate-attr="placeholder:rankings.filterByName">
      <button id="style-filter" type="button" aria-label="Toggle anime or realism characters">Anime</button>
      <button id="private-filter" data-translate="rankings.private">Private</button>
      <button id="favorite-filter" data-translate="rankings.favorite">Favorite</button>
      ${nsfwToggleHtml}
      <div class="gender-toggle-container">
        <div class="gender-toggle-header">
          <span class="female-text" data-translate="rankings.genderFemale">Female</span>
          <span class="male-text" data-translate="rankings.genderMale">Male</span>
        </div>
        <div class="gender-toggle" tabindex="0" role="button" aria-label="Toggle between female and male characters">
          <div class="gender-toggle-slider"></div>
        </div>
      </div>
      <!-- TODO: show card-animation-toggle once still/animated layout is ready -->
      <div class="gender-toggle-container card-animation-toggle-container" hidden>
        <div class="gender-toggle-header">
          <span class="still-text" data-translate="rankings.cardStill">Still</span>
          <span class="animated-text" data-translate="rankings.cardAnimated">Animated</span>
        </div>
        <div class="card-animation-toggle" tabindex="0" role="button" aria-label="Toggle still or animated ranking cards">
          <div class="gender-toggle-slider"></div>
        </div>
      </div>
    `;

    this.nameFilter = this.element.querySelector('#name-filter');
    this.styleFilter = this.element.querySelector('#style-filter');
    this.privateFilter = this.element.querySelector('#private-filter');
    this.favoriteFilter = this.element.querySelector('#favorite-filter');
    this.nsfwToggleContainer = this.element.querySelector('.nsfw-toggle-container');
    this.nsfwToggle = this.element.querySelector('.nsfw-toggle');
    this.genderToggleContainer = this.element.querySelector('.gender-toggle-container:not(.nsfw-toggle-container):not(.card-animation-toggle-container)');
    this.genderToggle = this.element.querySelector('.gender-toggle');
    this.cardAnimationToggleContainer = this.element.querySelector('.card-animation-toggle-container');
    this.cardAnimationToggle = this.element.querySelector('.card-animation-toggle');
    if (this.cardAnimationToggleContainer) this.cardAnimationToggleContainer.hidden = true;

    this.addEventListeners();
    this.eventListenersAdded = true;
    this.elementsCreated = true;
  }

  defaultFilters() {
    return {
      nameFilter: '',
      private: false,
      male: getGenderPreference(),
      style: getStylePreference(),
      favorite: false,
      nsfw: getNsfwEnabled()
    };
  }

  updateUIState() {
    if (!this.state.filters) {
      console.warn('FilterComponent.updateUIState: state.filters is undefined, initializing with defaults');
      this.state.filters = this.defaultFilters();
    }

    if (this.nameFilter && this.state.filters) {
      this.nameFilter.value = this.state.filters.nameFilter || '';
    }

    if (this.privateFilter) {
      this.privateFilter.classList.toggle('active', this.state.filters.private);
    }
    if (this.favoriteFilter) {
      this.favoriteFilter.classList.toggle('active', this.state.filters.favorite);
    }

    this.updateMaleFilterButton();
    this.updateStyleFilterButton();
    this.updateNsfwFilterButton();
    this.updateCardAnimationToggle();
  }

  updateTranslations() {
    applyTranslations('rankings', getCurrentLanguage());
    this.updateStyleFilterButton();
  }

  updateStyleFilterButton() {
    if (!this.styleFilter) return;
    const style = normalizeStyle(this.state.filters?.style);
    const lang = getCurrentLanguage();
    const rankingsT = window.translations?.[lang]?.rankings || window.translations?.en?.rankings || {};
    const label = style === 'realism'
      ? (rankingsT['rankings.styleRealism'] || 'Realism')
      : (rankingsT['rankings.styleAnime'] || 'Anime');
    this.styleFilter.textContent = label;
    this.styleFilter.classList.toggle('realism', style === 'realism');
    this.styleFilter.setAttribute('aria-pressed', String(style === 'realism'));
  }

  updateNsfwFilterButton() {
    const unrestricted = !!this.state.filters?.nsfw;
    this.nsfwToggleContainer?.classList.toggle('unrestricted', unrestricted);
    this.nsfwToggle?.classList.toggle('unrestricted', unrestricted);
    this.nsfwToggle?.setAttribute('aria-pressed', String(unrestricted));
  }

  updateMaleFilterButton() {
    const showingMales = !!this.state.filters?.male;
    this.genderToggleContainer?.classList.toggle('male', showingMales);
    this.genderToggle?.classList.toggle('male', showingMales);
  }

  updateCardAnimationToggle() {
    const animated = getCardAnimationEnabled();
    this.cardAnimationToggleContainer?.classList.toggle('animated', animated);
    this.cardAnimationToggle?.classList.toggle('animated', animated);
    this.cardAnimationToggle?.setAttribute('aria-pressed', String(animated));
  }

  addEventListeners() {
    if (this.nameFilter) {
      const nameInputHandler = () => {
        this.state.filters.nameFilter = this.nameFilter.value;
        this.applyFilters();
      };
      this.nameFilter._inputHandler = nameInputHandler;
      this.nameFilter.addEventListener('input', nameInputHandler);
    }

    if (this.styleFilter) {
      const styleClickHandler = () => {
        const current = normalizeStyle(this.state.filters?.style);
        this.state.filters.style = current === 'anime' ? 'realism' : 'anime';
        setStylePreference(this.state.filters.style);
        this.updateStyleFilterButton();
        this.applyFilters();
      };
      this.styleFilter._clickHandler = styleClickHandler;
      this.styleFilter.addEventListener('click', styleClickHandler);
      this.styleFilter.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          styleClickHandler();
        }
      });
    }

    if (this.privateFilter) {
      const privateClickHandler = () => {
        this.state.filters.private = !this.state.filters.private;
        this.privateFilter.classList.toggle('active', this.state.filters.private);

        if (this.state.filters.private && this.favoriteFilter) {
          this.state.filters.favorite = false;
          this.favoriteFilter.classList.remove('active');
        }

        this.applyFilters();
      };
      this.privateFilter._clickHandler = privateClickHandler;
      this.privateFilter.addEventListener('click', privateClickHandler);
      this.privateFilter.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          privateClickHandler();
        }
      });
    }

    if (this.favoriteFilter) {
      const favoriteClickHandler = () => {
        this.state.filters.favorite = !this.state.filters.favorite;
        this.favoriteFilter.classList.toggle('active', this.state.filters.favorite);

        if (this.state.filters.favorite && this.privateFilter) {
          this.state.filters.private = false;
          this.privateFilter.classList.remove('active');
        }

        this.applyFilters();
      };
      this.favoriteFilter._clickHandler = favoriteClickHandler;
      this.favoriteFilter.addEventListener('click', favoriteClickHandler);
      this.favoriteFilter.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          favoriteClickHandler();
        }
      });
    }

    const genderToggleClickHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.state.filters.male = !this.state.filters.male;

      const genderToggleEvent = new CustomEvent('genderToggleClicked', {
        detail: { gender: this.state.filters.male ? 'male' : 'female' }
      });
      document.dispatchEvent(genderToggleEvent);

      this.saveGenderPreference(this.state.filters.male);

      this.updateMaleFilterButton();
      this.applyFilters();
    };

    const handleGenderToggle = (e) => {
      if (e.type === 'click' || (e.type === 'keydown' && (e.key === 'Enter' || e.key === ' '))) {
        genderToggleClickHandler(e);
      }
    };

    if (this.genderToggle) {
      this.genderToggle._clickHandler = genderToggleClickHandler;
      this.genderToggle.addEventListener('click', handleGenderToggle);
      this.genderToggle.addEventListener('keydown', handleGenderToggle);
    }

    const applyNsfwToggle = async (unrestricted, { skipScroll = false } = {}) => {
      const previousOver18 = getOver18();
      const previousNsfw = getNsfwEnabled();
      const over18 = previousOver18 || unrestricted === true;
      const nsfw = unrestricted === true;
      this.state.filters.nsfw = nsfw;
      this.updateNsfwFilterButton();
      this.applyFilters({ skipScroll });
      const persistResult = await setAgeContentPrefs({ over18, nsfw, skipScroll });
      if (persistResult?.ok === false) {
        this.state.filters.nsfw = previousNsfw;
        this.updateNsfwFilterButton();
        this.applyFilters({ skipScroll });
      }
    };

    const revertNsfwToggle = () => {
      this.state.filters.nsfw = getNsfwEnabled();
      this.updateNsfwFilterButton();
    };

    const nsfwToggleClickHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (isSfwSite()) {
        showUnrestrictedBridge();
        return;
      }
      const nextUnrestricted = !this.state.filters.nsfw;
      if (!nextUnrestricted) {
        applyNsfwToggle(false);
        return;
      }
      if (getOver18()) {
        applyNsfwToggle(true);
        return;
      }
      this.updateNsfwFilterButton();
      const genderModal = new GenderPreferenceModal();
      void genderModal.show((_isMale, prefs = {}) => {
        if (isSfwSite()) {
          revertNsfwToggle();
          return;
        }
        if (prefs.over18) {
          applyNsfwToggle(true, { skipScroll: true });
          return;
        }
        revertNsfwToggle();
      });
    };

    const handleNsfwToggle = (e) => {
      if (e.type === 'click' || (e.type === 'keydown' && (e.key === 'Enter' || e.key === ' '))) {
        nsfwToggleClickHandler(e);
      }
    };

    if (this.nsfwToggle) {
      this.nsfwToggle._clickHandler = nsfwToggleClickHandler;
      this.nsfwToggle.addEventListener('click', handleNsfwToggle);
      this.nsfwToggle.addEventListener('keydown', handleNsfwToggle);
    }

    const cardAnimationClickHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setCardAnimationEnabled(!getCardAnimationEnabled());
      this.updateCardAnimationToggle();
    };
    if (this.cardAnimationToggle) {
      this.cardAnimationToggle.addEventListener('click', cardAnimationClickHandler);
      this.cardAnimationToggle.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          cardAnimationClickHandler(event);
        }
      });
    }
  }

  applyFilters({ skipScroll = false } = {}) {
    this.state.filters = {
      nameFilter: this.nameFilter?.value.trim() || '',
      private: this.privateFilter?.classList.contains('active') || false,
      favorite: this.favoriteFilter?.classList.contains('active') || false,
      male: this.state.filters?.male || false,
      style: normalizeStyle(this.state.filters?.style),
      nsfw: !!this.state.filters?.nsfw
    };

    this.updateMaleFilterButton();
    this.updateNsfwFilterButton();

    if (this.onFilterChange) {
      this.onFilterChange(this.state.filters, { skipScroll });
    } else {
      console.warn('onFilterChange callback is not defined');
    }
  }

  /**
   * Programmatically apply filters to match a target character for deep-link flows.
   * @param {{ name?: string, isPrivate?: boolean, isMale?: boolean }} options
   */
  applyCharacterNavigationFilters(options = {}) {
    const rawName = (options.name || '').trim();
    const normalizedName = isPlaceholderCharacterName(rawName) ? '' : rawName;
    const isPrivate = !!options.isPrivate;
    const isMale = !!options.isMale;

    this.state.filters = {
      ...this.state.filters,
      nameFilter: normalizedName,
      private: isPrivate,
      favorite: false,
      male: isMale
    };

    if (this.nameFilter) {
      this.nameFilter.value = normalizedName;
    }

    if (this.privateFilter) {
      this.privateFilter.classList.toggle('active', isPrivate);
    }

    if (this.favoriteFilter) {
      this.favoriteFilter.classList.remove('active');
    }

    this.updateMaleFilterButton();
    this.applyFilters();
  }

  resetFilters() {
    this.state.filters = this.defaultFilters();

    if (this.nameFilter) {
      this.nameFilter.value = this.state.filters.nameFilter;
    }

    if (this.privateFilter) {
      this.privateFilter.classList.remove('active');
    }
    if (this.favoriteFilter) {
      this.favoriteFilter.classList.remove('active');
    }

    this.updateMaleFilterButton();
    this.updateStyleFilterButton();
    this.updateNsfwFilterButton();
    this.applyFilters();
  }

}
